import * as React from 'react';

import { cn } from '@heidi/utils';

import { typographyVariant } from '../typography';

import {
  tableBodyClasses,
  tableCaptionClasses,
  tableCellClasses,
  tableCellContentClasses,
  tableCellFlexClasses,
  tableClasses,
  tableFooterClasses,
  tableHeadClasses,
  tableHeadInnerClasses,
  tableHeaderClasses,
  tableRowClasses,
  tableWrapperClasses,
} from './table.styles';
import type { TableCellProps, TableProps } from './table.types';

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    { className, wrapperClassName, variant = 'default', striped = false, ...props },
    ref
  ) => (
    <div className={cn(tableWrapperClasses, wrapperClassName)}>
      <table
        ref={ref}
        data-variant={variant}
        data-striped={striped || undefined}
        className={cn(tableClasses, className)}
        {...props}
      />
    </div>
  )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn(tableHeaderClasses, className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(tableBodyClasses, className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot ref={ref} className={cn(tableFooterClasses, className)} {...props} />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(tableRowClasses, props.onClick ? 'cursor-pointer' : '', className)}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      typographyVariant({ variant: 'p2Bold', className: 'leading-none' }),
      tableHeadClasses,
      props.onClick ? 'cursor-pointer' : '',
      className
    )}
    {...props}
  >
    <div className={cn(tableHeadInnerClasses)}>{props.children}</div>
  </th>
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, contentClassName, bare = false, children, ...props }, ref) => (
    <td ref={ref} className={cn(tableCellClasses, className)} {...props}>
      {bare ? null : (
        <div
          className={cn(
            typographyVariant({ variant: 'p2', className: 'leading-none' }),
            tableCellContentClasses
          )}
        >
          <div className={cn(tableCellFlexClasses, className, contentClassName)}>
            {children}
          </div>
        </div>
      )}
    </td>
  )
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn(tableCaptionClasses, className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
